import { Component, ViewEncapsulation, OnInit, signal } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogModule, DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { FormsModule } from '@angular/forms';
import { ChipModule } from 'primeng/chip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { HeadingComponent } from '../../../../../shared/components/heading/heading.component';
import { ProjectService } from '../../../../../shared/services/project.service';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../../../shared/services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ArchivePayload, Project, ProjectQueryParams, ProjectTypes } from '../project.interface';
import { ArchiveConfirmation } from './modals/archive/archive-confirmation.component';
import { UnpublishedArchiveConfirmationComponent } from './modals/archive/unpublished-archive-confirmation.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TooltipModule } from 'primeng/tooltip';
import { UnarchiveProjectComponent } from './modals/unarchive/unarchive-project/unarchive-project.component';
import { CompanyService } from '../../../../../shared/services/company.service';
import { Subscription } from 'rxjs';
import { SearchMarketService } from '../../../../../shared/services/search-market.service';
import { EmittersService } from '../../../../../shared/services/emitters.service';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';
import { HelperService } from '../../../../../shared/services/helper.service';
import { ClientPortalService } from '../../../../../shared/services/client-portal.service';
import { ClientPortalProjectStatus } from '../../../../../shared/interfaces/client-portal.interface';

@Component({
  selector: 'app-project-list',
  standalone: true,
  imports: [
    DropdownModule,
    ButtonModule,
    FormsModule,
    ChipModule,
    IconFieldModule,
    InputIconModule,
    HeadingComponent,
    InputTextModule,
    TableModule,
    CommonModule,
    DynamicDialogModule,
    ConfirmPopupModule,
    TooltipModule,
    PipesModule
  ],
  templateUrl: './project-listing.component.html',
  styleUrl: './project-listing.component.scss',
  providers: [DialogService],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectListComponent implements OnInit {
  constructor(
    private projectService: ProjectService,
    private userService: UserService,
    private routeParams: ActivatedRoute,
    private router: Router,
    public dialogService: DialogService,
    private toastService: ToastService,
    private companyService: CompanyService,
    private emittersService: EmittersService,
    private searchMarketService: SearchMarketService,
    public helperService :HelperService,
    private clientPortalService: ClientPortalService
  ) {
    this.clientMode.set(this.clientPortalService.isClientPortalUser());
  }
  /**
   * MP-5752 client mode: the list reads from the client-portal namespace and
   * shows no Create button, no search and no row actions. Derived once from
   * the session role. A CLIENT token cannot reach projects/ or companies/.
   */
  readonly clientMode = signal(false);
  cities: any;
  selectedCity: any;
  projects: any;
  projectsCount: number = 0;
  projectType!: string;
  projectsTypeTitle!: string;
  searchText!: string;
  searchedItems: string[] = [];
  companies: any = [];
  projectTypes: ProjectTypes = {
    PUBLISHED: 'PUBLISHED',
    UNPUBLISHED: 'CREATED',
    ARCHIVED: 'ARCHIVED',
    STARTED: 'STARTED',
  };

  headingButton: any = {
    url: '/client/project/create',
    label: 'CREATE PROJECT',
  };
  projectOwner!: string;
  searchMarketsSelecterEmitter: Subscription | undefined;

  async ngOnInit() {
    this.searchedItems = [];
    //this.headingButton.url = this.routeParams.snapshot.url
    this.routeParams.url.subscribe((value) => {
      this.projectType = value[0].path.toUpperCase();
      this.projectsTypeTitle =
        this.projectType[0].toUpperCase() +
        this.projectType.substring(1).toLowerCase() +
        ' Projects';
    });
    this.getProjects({ first: 0, rows: 10 });
    if (this.clientMode()) {
      return;
    }
    this.companies = await this.companyService.getCompanies();
    this.subscribeEvents();
  }

  /** Client mode: the portal list, scoped by the backend to the user's group; the table shape is the same `{projects, count}`. */
  private getClientPortalProjects(offset: number, limit: number): void {
    const status = this.projectTypes[this.projectType as keyof typeof this.projectTypes] as ClientPortalProjectStatus;
    this.clientPortalService.getProjects({ status, offset, limit, sort: '-updatedAt' }).subscribe({
      next: (response) => {
        this.projects = response.projects;
        this.projectsCount = response.count;
      },
    });
  }

  ref: DynamicDialogRef | undefined;

  showArchiveConfirmation($event: Event, project: any) {
    $event.stopPropagation();

    if (this.projectType === 'UNPUBLISHED') {
      this.ref = this.dialogService.open(UnpublishedArchiveConfirmationComponent, {
        header: 'Confirmation',
        width: '30vw',
        contentStyle: { overflow: 'auto' },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw',
        },
      });

      this.ref.onClose.subscribe((data: { endMessage: string; action: 'archive' | 'delete' }) => {
        if (!data) return;
          this.projectService
            .projectArchive(project._id, { endMessage: data.endMessage, isDelete:  data.action === 'archive' ? false : true})
            .subscribe(() => this.getProjects({ first: 0, rows: 10 }));
      });
      return;
    }

    this.ref = this.dialogService.open(ArchiveConfirmation, {
      header: 'Confirmation',
      width: '30vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
    });

    this.ref.onClose.subscribe((data: ArchivePayload) => {
      if (data) {
        this.projectService
          .projectArchive(project._id, data)
          .subscribe((response) => {
            this.getProjects({ first: 0, rows: 10 });
          });
      }
    });
  }

  ngOnDestroy() {
    if (this.ref) {
      this.ref.close();
    }
    this.searchMarketsSelecterEmitter && this.searchMarketsSelecterEmitter.unsubscribe();
  }

  subscribeEvents(){
    this.searchMarketsSelecterEmitter = this.emittersService.searchMarketSelectorEmitter.subscribe(()=>{
      this.getProjects({ first: 0, rows: 10 });
    });
  }

  clearProjectSearch() {
    this.searchedItems = [];
    this.searchText = '';
    this.getProjects({ first: 0, rows: 10 });
  }
  onEnter($event: any) {
    if ($event.keyCode === 13) {
      this.getProjects({ first: 0, rows: 10 });
    }
  }
  getProjects(event?: any) {
    let offset = event.first;
    let limit = event.rows;
    if (this.clientMode()) {
      this.getClientPortalProjects(offset, limit);
      return;
    }
    const searchMarkets = this.searchMarketService.getSearchMarkets()
    const user = this.userService.getSessionUser();
    const type = this.projectType;
    let queryParams: ProjectQueryParams = {
      status: this.projectTypes[type as keyof typeof this.projectTypes],
      sessionUserId: user._id,
      offset: offset,
      limit: limit
    };
    const payload = {
      markets: searchMarkets || 'all',
    };
    if (this.searchText) {
      this.searchedItems = [this.searchText];
      queryParams.searchText = this.searchedItems[0];
      queryParams.searchBy = 'project';
    }

    this.projectService.getProjectsWithMarketFilter(payload, queryParams).subscribe({
      next: (response) => {
        this.projects = response.projects;
        this.projectsCount = response.count;
      },
    });
  }

  goToProjectDetails(project: any) {
    this.router.navigateByUrl(
      `/client/project/${this.projectType.toLowerCase()}/${project._id}/details`,
      {
        state: {
          data: {bundle: project.bundle}
        }
      }
    );
  }

  projectArchive(project: any) {
    this.projectService.projectArchive(project._id, { endMessage: '' });
  }

  projectRestore($event: Event, project: any) {
    $event.stopPropagation();
    this.ref = this.dialogService.open(UnarchiveProjectComponent, {
      header: 'Restart Project',
      width: '30vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: [project.owner, project.codeName],
    });

    this.ref.onClose.subscribe((data) => {
      if (data) {
        this.projectService
          .projectRestore(project._id, { projectOwner: data.value })
          .subscribe((response) => {
            this.getProjects({ first: 0, rows: 10 });

            this.toastService.showSuccess('Project restored');
          });
      }
    });
  }

  onEdit($event: Event, project: Project): void {
    $event.stopPropagation();
    this.router.navigateByUrl(`/client/project/${project._id}/edit`);
  }

  clientName(client: any): string {
    return this.companies.filter((company: any) => company._id === client)[0]?.name || client
  }
}
